import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { NoAvailability } from 'components/OnlineBooking/Steps/SelectStaff/NoAvailability/NoAvailability';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getStaffs } from 'reducers/staffs/StaffsSelectors';
import { dispatch } from 'store';
import { Staff } from 'types/Staff';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { StaffSelector } from 'components/OnlineBooking/Grooming/StaffList/StaffSelector/StaffSelector';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingGroomingItems,
    getOnlineBookingGroomingPreferredStaff
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { getBusinessArea, trackStaffSelected } from 'utils/analytics';

export const OnlineBookingGroomingStaffListStep: React.FC = React.memo(() => {
    const marketplace = useMarketplace();

    const staffs = useSelector(getStaffs);
    const groomingBookingItems = useSelector(getOnlineBookingGroomingItems);
    const preferredStaff = useSelector(getOnlineBookingGroomingPreferredStaff);

    const availableStaffs = React.useMemo(() => {
        return staffs.filter(staff => {
            return groomingBookingItems.every(item => {
                return item.service?.staffIds.includes(staff.id);
            });
        });
    }, [groomingBookingItems, staffs]);

    const backHandler = React.useCallback(() => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceList));
    }, []);

    const changePreferredStaffHandler = React.useCallback(
        (staff?: Staff) => {
            dispatch(
                updateOnlineBookingBookingItems([
                    ...groomingBookingItems.map(item => ({
                        ...item,
                        preferredStaff: staff
                    }))
                ])
            );
        },
        [groomingBookingItems]
    );

    const submitHandler = React.useCallback(() => {
        dispatch(
            updateOnlineBookingBookingItems([
                ...groomingBookingItems.map(item => ({
                    ...item,
                    // Set the selected staff
                    staff: preferredStaff
                }))
            ])
        );

        // Analytics
        trackStaffSelected({
            marketplace_slug: marketplace.slug,
            business_area: getBusinessArea(groomingBookingItems),
            staff_preference: preferredStaff ? 'specific' : 'no_preference'
        });

        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    }, [groomingBookingItems, preferredStaff, marketplace.slug]);

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h1 underlined w-full mb-6">Select staff</h1>

                {availableStaffs.length === 0 && <NoAvailability />}

                {availableStaffs.length > 0 && (
                    <StaffSelector
                        preferredStaff={preferredStaff}
                        staffs={availableStaffs}
                        onChange={changePreferredStaffHandler}
                    />
                )}

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        disabled={availableStaffs.length === 0}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
});

OnlineBookingGroomingStaffListStep.displayName = 'OnlineBookingGroomingStaffListStep';
